import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useEstimativaWindx from 'helper/windx/useEstimativaWindx';
import {
  selectWindxFiltrosCompletos,
  selectWindxConsultaPendente,
  realizarConsulta,
} from 'redux/reducers/windxControlPanelReducer';

import './index.scss';

interface BarraConsultaProps {
  /** `true` enquanto a query de dados da tela esta em voo. */
  carregando: boolean;
  onAbrirPainel: () => void;
}

/**
 * Barra fixa de consulta do Windx mobile (spec Windx mobile, D3/D4).
 *
 * No desktop o botao "Realizar Consulta" vive no rodape do painel lateral, que
 * esta sempre visivel. No mobile o painel e um BottomSheet — deixar o botao la
 * dentro obrigaria a abrir o painel so para disparar a consulta. Entao ele sobe
 * para esta barra, no alcance do polegar, junto do estado atual da consulta.
 *
 * Ela tambem absorve os avisos que no desktop sao blocos no corpo da tela
 * (`AvisoFiltrosAlterados` e `AvisoConsultaInviavel`), que no mobile
 * empilhariam no scroll.
 *
 * Precedencia dos estados: carregando > inviavel > incompleta > pendente.
 * Com a consulta em dia, a barra nao renderiza nada.
 *
 * No estado inviavel a barra NAO oferece o atalho de agregacao: a escolha da
 * subdivisao de escape depende de `estimativa.sugestoes` e ja esta resolvida no
 * `MedidorComplexidade`, dentro do painel. Duplicar aqui criaria duas fontes de
 * verdade — a barra so leva o usuario ate la.
 */
export default function BarraConsulta({ carregando, onAbrirPainel }: BarraConsultaProps) {
  const dispatch = useDispatch();
  const filtrosCompletos = useSelector(selectWindxFiltrosCompletos);
  const consultaPendente = useSelector(selectWindxConsultaPendente);
  const { estimativa } = useEstimativaWindx();

  const inviavel = estimativa?.veredito === 'vermelho';

  // Consulta em dia: nada a pedir ao usuario, a barra some.
  const emDia = !carregando && !inviavel && filtrosCompletos && !consultaPendente;
  if (emDia) return null;

  let titulo: string;
  let subtitulo: string;
  let rotulo: string;
  let acao: () => void;
  let variante: 'confirm' | 'secondary' = 'confirm';
  let desabilitado = false;

  if (carregando) {
    titulo = 'Consultando';
    subtitulo = 'Buscando os dados da seleção atual…';
    rotulo = 'Consultando…';
    acao = () => {};
    desabilitado = true;
  } else if (inviavel) {
    titulo = 'Consulta inviável';
    subtitulo = `${estimativa?.n_series ?? 0} séries — reduza a seleção`;
    rotulo = 'Reduzir no painel';
    acao = onAbrirPainel;
    variante = 'secondary';
  } else if (!filtrosCompletos) {
    titulo = 'Consulta incompleta';
    subtitulo = 'Falta preencher filtros obrigatórios';
    rotulo = 'Abrir painel';
    acao = onAbrirPainel;
    variante = 'secondary';
  } else {
    titulo = 'Filtros alterados';
    subtitulo = 'Atualize para ver os dados novos';
    rotulo = 'Realizar consulta';
    acao = () => dispatch(realizarConsulta());
  }

  return (
    <div className={`windx-barra-consulta${inviavel ? ' is-inviavel' : ''}`}>
      <div className="windx-barra-consulta-texto">
        <span className="windx-barra-consulta-titulo">{titulo}</span>
        <span className="windx-barra-consulta-subtitulo">{subtitulo}</span>
      </div>
      <SheetActionButton
        variant={variante}
        onClick={acao}
        disabled={desabilitado}
        loading={carregando}
        className="windx-barra-consulta-acao"
      >
        {rotulo}
      </SheetActionButton>
    </div>
  );
}
